<template>
  <div>
    <!-- Mockup bar -->
    <div class="mockup-bar">
      <span class="mockup-badge">MOCKUP</span>
      <span class="mockup-bar-sep">·</span>
      <span class="mockup-bar-label">Home Page — Browse Visits wireframe</span>
      <router-link to="/" class="mockup-back">← Back to app</router-link>
    </div>

    <div class="content-box" v-if="step === 'home'">
      <div class="prompt">
        <div class="heading">ACT Interviewer App Home Page</div>
        <p class="question">What would you like to do?</p>
        <div class="button-container">
          <button class="act-btn dark" @click="step = 'home'">Select a Participant</button>
          <button class="act-btn dark" @click="step = 'home'">Export Data</button>
          <button class="act-btn dark" @click="step = 'browse'">Browse Visits</button>
        </div>
      </div>
    </div>

    <!-- Browse Visits view -->
    <div v-if="step === 'browse'" class="browse-wrap">
      <div class="browse-header">
        <button class="back-link" @click="step = 'home'">← Back</button>
        <div class="heading">Browse Visits</div>
      </div>

      <!-- Participant selector -->
      <div class="browse-controls">
        <label class="subject-label">Participant</label>
        <select class="subject-select" v-model="selectedParticipant">
          <option value="">All participants</option>
          <option v-for="p in participants" :key="p.id" :value="p.id">{{ p.id }}</option>
        </select>
      </div>

      <!-- Visit list + detail pane -->
      <div class="browse-layout">

        <!-- Visit list -->
        <div class="visit-list">
          <div
            v-for="visit in filteredVisits"
            :key="visit.id"
            class="visit-row"
            :class="{ active: selectedVisit && selectedVisit.id === visit.id }"
            @click="selectVisit(visit)"
          >
            <div class="visit-row-top">
              <span class="visit-pid">{{ visit.participantId }}</span>
              <span class="visit-type">{{ visit.type }}</span>
            </div>
            <div class="visit-row-bottom">
              <span class="visit-date">{{ visit.date }}</span>
              <span class="visit-status" :class="visit.status">{{ visit.statusLabel }}</span>
            </div>
          </div>
        </div>

        <!-- Detail pane -->
        <div class="visit-detail" v-if="selectedVisit">

          <!-- Detail header -->
          <div class="detail-header">
            <div>
              <div class="detail-title">{{ selectedVisit.participantId }} — {{ selectedVisit.type }}</div>
              <div class="detail-sub">{{ selectedVisit.date }}</div>
            </div>
            <span class="visit-status" :class="selectedVisit.status">{{ selectedVisit.statusLabel }}</span>
          </div>

          <!-- Tabs -->
          <div class="tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="tab-btn"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >{{ tab.label }}</button>
          </div>

          <!-- Tab: Tasks -->
          <div v-if="activeTab === 'tasks'" class="tab-panel">
            <table class="task-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Status</th>
                  <th>Open requests</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in selectedVisit.tasks" :key="task.name">
                  <td>{{ task.name }}</td>
                  <td><span class="task-status" :class="task.status">{{ task.statusLabel }}</span></td>
                  <td>
                    <span v-if="task.openRequests > 0" class="open-req-chip">⚑ {{ task.openRequests }}</span>
                    <span v-else class="none-chip">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Tab: History / Changes -->
          <div v-if="activeTab === 'history'" class="tab-panel">
            <div v-if="selectedVisit.history.length === 0" class="empty-state">No changes recorded yet.</div>
            <div
              v-for="event in selectedVisit.history"
              :key="event.id"
              class="history-event"
            >
              <div class="he-left">
                <div class="he-avatar" :style="{ background: event.actorColor }">{{ event.initials }}</div>
                <div class="he-spine"></div>
              </div>
              <div class="he-body">
                <div class="he-meta">
                  <span class="he-actor">{{ event.actor }}</span>
                  <span class="he-tag" :class="event.type">{{ eventLabel(event.type) }}</span>
                  <span class="he-time">{{ event.time }}</span>
                </div>
                <div class="he-text">{{ event.text }}</div>
                <div v-if="event.from || event.to" class="he-change">
                  <span class="he-mark" :class="event.from">{{ event.from || '—' }}</span>
                  <span class="he-arrow">→</span>
                  <span class="he-mark" :class="event.to">{{ event.to || '—' }}</span>
                  <span class="he-task-ref">{{ event.task }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Comments -->
          <div v-if="activeTab === 'comments'" class="tab-panel">
            <div v-if="selectedVisit.comments.length === 0" class="empty-state">No comments yet.</div>
            <div
              v-for="c in selectedVisit.comments"
              :key="c.id"
              class="comment-item"
            >
              <div class="comment-avatar" :style="{ backgroundColor: c.avatarColor }">{{ c.initials }}</div>
              <div class="comment-body">
                <div class="comment-meta">
                  <span class="comment-author">{{ c.author }}</span>
                  <span class="comment-task">{{ c.task }}</span>
                  <span class="comment-time">{{ c.time }}</span>
                </div>
                <div class="comment-text">{{ c.text }}</div>
              </div>
            </div>
          </div>

        </div>

        <div class="visit-detail empty-detail" v-else>
          <p>Select a visit to see details.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WireframesHomePage',
  data() {
    return {
      step: 'home',
      selectedParticipant: '',
      selectedVisit: null,
      activeTab: 'tasks',
      tabs: [
        { id: 'tasks',   label: 'Tasks' },
        { id: 'history', label: 'History / Changes' },
        { id: 'comments', label: 'Comments' }
      ],
      participants: [
        { id: 'ACT001' },
        { id: 'ACT002' },
        { id: 'ACT003' }
      ],
      visits: [
        {
          id: 1, participantId: 'ACT001', type: 'Biennial',
          date: 'Feb 10, 2025', interviewer: 'Interviewer',
          status: 'complete', statusLabel: 'Complete',
          tasks: [
            { name: 'VF — Supermarket', status: 'scored', statusLabel: 'Scored', score: 18, openRequests: 1 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Feb 10 · 9:22 AM', text: 'Scored VF — Supermarket. apple juice marked C.', from: null, to: null, task: '' },
            { id: 'h2', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'comment',     time: 'Feb 10 · 9:25 AM', text: 'Added comment on VF — Supermarket: "Participant paused before apple juice."', from: null, to: null, task: '' },
            { id: 'h3', actor: 'Reviewer',    initials: 'RV', actorColor: '#7c3aed', type: 'request',     time: 'Feb 12 · 2:14 PM', text: 'Requested score change on VF — Supermarket: apple juice.', from: 'C', to: 'I', task: 'VF — Supermarket' },
            { id: 'h4', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'accepted',    time: 'Feb 13 · 10:01 AM', text: 'Accepted change request for VF — Supermarket: apple juice.', from: 'C', to: 'I', task: 'VF — Supermarket' },
          ],
          comments: [
            { id: 'c1', author: 'Interviewer', initials: 'IN', avatarColor: '#317391', task: 'VF — Supermarket', time: 'Feb 10 · 9:25 AM', text: 'Participant paused before apple juice — worth reviewing.' },
          ]
        },
        {
          id: 2, participantId: 'ACT001', type: 'Biennial',
          date: 'Mar 4, 2023', interviewer: 'Interviewer',
          status: 'complete', statusLabel: 'Complete',
          tasks: [
            { name: 'VF — Supermarket', status: 'scored', statusLabel: 'Scored', score: 17, openRequests: 0 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Mar 4 · 10:00 AM', text: 'Scored VF — Supermarket.', from: null, to: null, task: '' },
          ],
          comments: []
        },
        {
          id: 3, participantId: 'ACT002', type: 'Biennial',
          date: 'Feb 14, 2025', interviewer: 'Interviewer',
          status: 'in-progress', statusLabel: 'In Progress',
          tasks: [
            { name: 'VF — Supermarket', status: 'pending', statusLabel: 'Pending', score: null, openRequests: 0 },
          ],
          history: [],
          comments: []
        },
        {
          id: 4, participantId: 'ACT003', type: 'Biennial',
          date: 'Feb 18, 2025', interviewer: 'Interviewer',
          status: 'pending', statusLabel: 'Pending',
          tasks: [
            { name: 'VF — Supermarket', status: 'pending', statusLabel: 'Pending', score: null, openRequests: 0 },
          ],
          history: [],
          comments: []
        }
      ]
    };
  },
  computed: {
    filteredVisits() {
      if (!this.selectedParticipant) return this.visits;
      return this.visits.filter(v => v.participantId === this.selectedParticipant);
    }
  },
  methods: {
    selectVisit(visit) {
      this.selectedVisit = visit;
      this.activeTab = 'tasks';
    },
    eventLabel(type) {
      return {
        'score-entry': 'Score Entry',
        'comment':     'Comment',
        'request':     'Change Request',
        'accepted':    'Accepted',
        'rejected':    'Rejected',
        'edit':        'Edit'
      }[type] || type;
    }
  }
};
</script>

<style scoped>
/* ── Mockup bar ─────────────────────────────────────────── */
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}
.mockup-badge {
  font-size: 10px;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
}
.mockup-bar-sep { color: #d1d5db; }
.mockup-bar-label { color: #9ca3af; }
.mockup-back { margin-left: auto; color: #9ca3af; text-decoration: none; font-size: 12px; }
.mockup-back:hover { color: #64748b; }

/* ── Home step — mirrors real HomePage exactly ──────────── */
.heading {
  font-weight: normal;
  font-size: 30px;
  margin-bottom: 50px;
  color: #237091;
  text-align: center;
}
.content-box {
  background-color: #F4F4F4;
  padding: 40px;
  border-radius: 6px;
  border: 1px solid #a0a0a0;
  max-width: 600px;
  height: 450px;
  margin: 0 auto;
  margin-top: 50px;
  position: relative;
}
.prompt { text-align: center; }
.question { font-size: 20px; margin-bottom: 20px; }
.button-container { display: flex; flex-direction: column; gap: 15px; align-items: center; }
.act-btn {
  width: 200px;
  height: 40px;
  padding: 0 20px;
  background-color: white;
  border: 2px solid #3986A8;
  color: #3986A8;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}
.act-btn.dark { background-color: #3986A8; color: white; }
.act-btn:hover { opacity: 0.88; }

/* ── Browse header ──────────────────────────────────────── */
.browse-wrap {
  max-width: 1100px;
  margin: 30px auto;
  padding: 0 20px;
}
.browse-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}
.browse-header .heading { margin-bottom: 0; }
.back-link {
  background: none;
  border: none;
  color: #3986A8;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}
.back-link:hover { text-decoration: underline; }

/* ── Controls ───────────────────────────────────────────── */
.browse-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.subject-label { font-size: 14px; color: #333; white-space: nowrap; }
.subject-select {
  padding: 8px 10px;
  font-size: 14px;
  border-radius: 8px;
  border: 2px solid #3986A8;
  background-color: white;
  min-width: 180px;
}
.subject-select:focus { outline: none; border-color: #237091; }

/* ── Browse layout ──────────────────────────────────────── */
.browse-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 16px;
  align-items: start;
}

/* ── Visit list ─────────────────────────────────────────── */
.visit-list {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  overflow: hidden;
  background: #F4F4F4;
}
.visit-row {
  padding: 12px 14px;
  border-bottom: 1px solid #d1d5db;
  cursor: pointer;
  transition: background 0.12s;
}
.visit-row:last-child { border-bottom: none; }
.visit-row:hover { background: #e8f4f8; }
.visit-row.active { background: #d0eaf4; border-left: 3px solid #3986A8; }
.visit-row-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.visit-pid { font-weight: bold; font-size: 14px; color: #237091; }
.visit-type { font-size: 12px; color: #555; }
.visit-row-bottom { display: flex; justify-content: space-between; align-items: center; }
.visit-date { font-size: 12px; color: #777; }

/* ── Status chips ───────────────────────────────────────── */
.visit-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.visit-status.complete     { background: #dcfce7; color: #166534; }
.visit-status.in-progress  { background: #fef3c7; color: #92400e; }
.visit-status.pending      { background: #f1f5f9; color: #475569; }

/* ── Visit detail pane ──────────────────────────────────── */
.visit-detail {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  background: #F4F4F4;
  overflow: hidden;
}
.visit-detail.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #aaa;
  font-size: 14px;
}
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 18px 12px;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.detail-title { font-size: 16px; font-weight: bold; color: #237091; margin-bottom: 4px; }
.detail-sub { font-size: 12px; color: #777; }

/* ── Tabs ───────────────────────────────────────────────── */
.tabs {
  display: flex;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.tab-btn {
  padding: 9px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  background: none;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn:hover { color: #237091; }
.tab-btn.active { color: #237091; border-bottom-color: #3986A8; font-weight: 600; }

/* ── Tab panel ──────────────────────────────────────────── */
.tab-panel { padding: 16px 18px; }

/* ── Task table ─────────────────────────────────────────── */
.task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.task-table th {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 2px solid #d1d5db;
  color: #555;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.task-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  color: #333;
}
.task-table tr:last-child td { border-bottom: none; }
.task-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.task-status.scored  { background: #dcfce7; color: #166534; }
.task-status.pending { background: #f1f5f9; color: #475569; }
.score-cell { font-weight: 600; color: #237091; }
.open-req-chip { font-size: 11px; font-weight: 600; background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 10px; }
.none-chip { color: #aaa; font-size: 13px; }

/* ── History events ─────────────────────────────────────── */
.empty-state { color: #aaa; font-size: 14px; text-align: center; padding: 24px 0; }
.history-event { display: flex; gap: 12px; margin-bottom: 16px; }
.history-event:last-child .he-spine { display: none; }
.he-left { display: flex; flex-direction: column; align-items: center; }
.he-avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: white;
}
.he-spine { flex: 1; width: 2px; background: #e5e7eb; margin-top: 4px; min-height: 16px; }
.he-body { flex: 1; padding-bottom: 4px; }
.he-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; flex-wrap: wrap; }
.he-actor { font-weight: 600; font-size: 13px; color: #111; }
.he-tag {
  font-size: 11px; font-weight: 600; padding: 1px 7px; border-radius: 10px;
}
.he-tag.score-entry { background: #dbeafe; color: #1d4ed8; }
.he-tag.comment     { background: #cffafe; color: #0e7490; }
.he-tag.request     { background: #fef3c7; color: #92400e; }
.he-tag.accepted    { background: #dcfce7; color: #166534; }
.he-tag.rejected    { background: #fee2e2; color: #991b1b; }
.he-tag.edit        { background: #ede9fe; color: #5b21b6; }
.he-time { font-size: 11px; color: #9ca3af; margin-left: auto; }
.he-text { font-size: 13px; color: #374151; margin-bottom: 4px; }
.he-change {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 6px; padding: 3px 10px; font-size: 12px;
}
.he-mark {
  font-weight: 700; padding: 1px 6px; border-radius: 4px;
  background: #f1f5f9; color: #475569;
}
.he-mark.C { background: #dcfce7; color: #166534; }
.he-mark.I { background: #fee2e2; color: #991b1b; }
.he-mark.R { background: #fef3c7; color: #92400e; }
.he-mark.Q { background: #e0f2fe; color: #075985; }
.he-arrow { color: #94a3b8; }
.he-task-ref { font-size: 11px; color: #94a3b8; margin-left: 4px; }

/* ── Comments ───────────────────────────────────────────── */
.comment-item {
  display: flex; gap: 10px; margin-bottom: 14px;
  padding: 12px 14px; background: white;
  border: 1px solid #e5e7eb; border-radius: 8px;
}
.comment-avatar {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: white;
}
.comment-body { flex: 1; min-width: 0; }
.comment-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; }
.comment-author { font-weight: 600; font-size: 13px; color: #111; }
.comment-task { font-size: 11px; background: #f1f5f9; color: #475569; padding: 1px 7px; border-radius: 8px; }
.comment-time { font-size: 11px; color: #9ca3af; margin-left: auto; }
.comment-text { font-size: 13px; color: #374151; }
</style>
